import { freeTextError, isAllowedFreeTextChar } from '@/utils/freeTextValidation'
import { FieldMarker, FieldRequiredRail } from '@/components/common/FieldMarkers'
import { resolveFieldMarkerState } from '@/components/common/fieldMarkerState'
import { ArrowRight, Check, Close, ExclamationCircle } from 'flowbite-react-icons/outline'
import { AnimatePresence, motion } from 'framer-motion'
import { forwardRef, useCallback, useEffect, useId, useRef, useState } from 'react'
import { lockBodyScroll, unlockBodyScroll } from '@/components/common/bodyScrollLock'
import { useDialogFocusTrap } from '@/components/common/useDialogFocusTrap'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  footer?: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  hidden?: boolean
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
}

const ANIMATION_DURATION = 0.24

const modalVariants = {
  enter: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: ANIMATION_DURATION, ease: [0.32, 0.72, 0, 1] },
  },
  exit: {
    opacity: 0,
    y: 60,
    scale: 0.98,
    transition: { duration: ANIMATION_DURATION, ease: [0.32, 0.72, 0, 1] },
  },
  initial: {
    opacity: 0,
    y: 60,
    scale: 0.98,
  },
  hidden: {
    opacity: 0,
    y: -60,
    scale: 0.98,
    transition: { duration: ANIMATION_DURATION, ease: [0.32, 0.72, 0, 1] },
  },
}

const backdropVariants = {
  enter: { opacity: 1, transition: { duration: ANIMATION_DURATION } },
  exit: { opacity: 0, transition: { duration: ANIMATION_DURATION } },
  initial: { opacity: 0 },
  hidden: { opacity: 0, transition: { duration: ANIMATION_DURATION } },
}

// SPA fork of Helix Modal: owns ref-counted bodyScrollLock + dialog focus trap.
// Prefer fixing Helix upstream over growing this kitchen-sink file further.
export function Modal({ isOpen, onClose, title, children, footer, size = 'lg', hidden = false }: ModalProps) {
  const titleId = useId()
  const dialogRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    // Lock body scroll only while this modal is the visible top layer. Multiple
    // Modals can be mounted at once (a base modal with `hidden` set behind a
    // nested search modal), so instead of unconditionally clearing the lock on
    // cleanup — which lets one modal's teardown wipe a lock another still needs
    // — we reference-count the number of modals requesting the lock and only
    // release the body style when the count returns to zero.
    if (!isOpen || hidden) return

    lockBodyScroll()
    return () => {
      unlockBodyScroll()
    }
  }, [isOpen, hidden])

  useDialogFocusTrap({
    isOpen,
    isCovered: hidden,
    dialogRef,
    onEscape: onClose,
  })

  // `inert` is valid HTML but missing from React 18's JSX typings; set it on the
  // dialog node so a covered parent cannot receive focus while HPO search is open.
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (hidden) {
      dialog.setAttribute('inert', '')
    } else {
      dialog.removeAttribute('inert')
    }
  }, [hidden, isOpen])

  return (
    // NOTE: no `mode="wait"` here. `mode="wait"` defers new content until an
    // exiting child finishes and is meant for swapping *keyed* children. With a
    // single conditionally-rendered child that also animates to a `hidden`
    // (opacity: 0) state, toggling `isOpen` off while already hidden produced a
    // degenerate exit transition that never signaled completion — so
    // AnimatePresence kept the (invisible) backdrop mounted. That stranded
    // `fixed inset-0 z-50 backdrop-blur-sm` layer is the "gray overlay stuck on
    // screen" and it also swallowed every click. Default (sync) mode lets the
    // exit run and unmount reliably.
    <AnimatePresence>
      {isOpen && (
        <div
          className={`fixed inset-0 z-50 flex items-stretch justify-center p-0 sm:items-center sm:p-4 ${hidden ? 'pointer-events-none' : ''}`}
        >
          <motion.div
            variants={backdropVariants}
            initial="initial"
            animate={hidden ? 'hidden' : 'enter'}
            exit="exit"
            className={`absolute inset-0 bg-black/50 backdrop-blur-sm ${hidden ? 'pointer-events-none' : ''}`}
            onClick={hidden ? undefined : onClose}
            aria-hidden="true"
          />

          <motion.div
            variants={modalVariants}
            initial="initial"
            animate={hidden ? 'hidden' : 'enter'}
            exit="exit"
            className={`relative z-10 flex h-full w-full flex-col sm:h-auto ${sizeClasses[size]}`}
          >
            <div
              ref={dialogRef}
              // When covered by a nested portal (HPO search), drop dialog
              // modality and hide from AT so the portaled UI owns the layer.
              role={hidden ? undefined : 'dialog'}
              aria-modal={hidden ? undefined : true}
              aria-hidden={hidden || undefined}
              aria-labelledby={hidden ? undefined : titleId}
              tabIndex={-1}
              className="flex h-full max-h-full flex-col bg-white shadow-2xl outline-none sm:h-auto sm:max-h-[88vh] sm:rounded-xl sm:border sm:border-border-base dark:bg-gray-900 dark:sm:border-gray-700"
            >
              <div className="flex shrink-0 items-center justify-between border-b border-border-base px-4 py-4 sm:px-6 sm:py-5 dark:border-gray-700">
                <h2
                  id={titleId}
                  className="text-lg font-semibold tracking-tight text-text-heading sm:text-2xl dark:text-white"
                >
                  {title}
                </h2>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="flex h-8 w-8 items-center justify-center rounded-full text-text-body transition-colors hover:bg-background-gray hover:text-text-heading sm:h-10 sm:w-10 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
                >
                  <Close className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>

              <div className="min-h-[200px] flex-1 overflow-y-auto overflow-x-visible px-4 py-4 sm:min-h-[300px] sm:px-6 sm:py-6">
                {children}
              </div>

              {footer && (
                <div className="shrink-0 border-t border-border-base px-4 py-4 sm:px-6 sm:py-6 dark:border-gray-700">
                  {footer}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

interface RadioOptionProps {
  name: string
  value: string
  checked: boolean
  onChange: (value: string) => void
  label: string
  description?: string
  first?: boolean
  last?: boolean
  /** `inline`: side-by-side cards (e.g. sample type). Default: stacked column. */
  layout?: 'stack' | 'inline'
  /** GEN-4471: When true, the radio is disabled and cannot be selected. */
  disabled?: boolean
  /** GEN-4471: Helper text shown below the description, typically a block reason. */
  helperText?: string
}

export function RadioOption({
  name,
  value,
  checked,
  onChange,
  label,
  description,
  first,
  last,
  layout = 'stack',
  disabled = false,
  helperText,
}: RadioOptionProps) {
  const roundedClasses =
    layout === 'inline'
      ? first && last
        ? 'rounded-xl'
        : first
          ? 'rounded-l-xl rounded-r-none'
          : last
            ? 'rounded-r-xl rounded-l-none'
            : 'rounded-none'
      : first && last
        ? 'rounded-xl'
        : first
          ? 'rounded-t-xl'
          : last
            ? 'rounded-b-xl'
            : ''
  const inlineOverlap = layout === 'inline' && !first ? '-ml-px' : ''

  return (
    <label
      className={`hover:bg-background-gray/50 flex min-h-0 flex-1 cursor-pointer gap-2.5 border border-border-base bg-white p-4 shadow-xs transition-colors dark:border-gray-700 dark:bg-gray-900 ${roundedClasses} ${inlineOverlap} ${checked ? 'relative z-[1] border-brand-600 dark:border-brand-500' : ''} ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
    >
      <div
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
          checked ? 'border-brand-600 bg-white' : 'border-border-base bg-background-gray'
        }`}
      >
        {checked && <div className="h-2 w-2 rounded-full bg-brand-600" />}
      </div>
      <div className="flex flex-1 flex-col gap-0.5">
        <span
          className={`text-text-heading ${
            description ? 'text-base font-semibold leading-5' : 'text-sm font-medium leading-4'
          }`}
        >
          {label}
        </span>
        {description && <span className="text-xs text-text-body">{description}</span>}
        {helperText && <span className="text-xs text-text-body-subtle italic">{helperText}</span>}
      </div>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => !disabled && onChange(value)}
        className="sr-only"
      />
    </label>
  )
}

interface CheckboxOptionProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
  description?: string
  disabled?: boolean
}

export function CheckboxOption({ checked, onChange, label, description, disabled }: CheckboxOptionProps) {
  return (
    <label
      className={`hover:bg-background-gray/50 flex cursor-pointer gap-2.5 rounded-xl border border-border-base bg-white p-4 shadow-xs transition-colors ${
        disabled ? 'cursor-not-allowed opacity-50' : ''
      }`}
    >
      <div
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded ${
          checked ? 'border-brand-600 bg-brand-600' : 'border border-border-base bg-background-gray'
        }`}
      >
        {checked && (
          <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-0.5">
        <span className="text-sm font-medium leading-4 text-text-heading">{label}</span>
        {description && <span className="text-xs text-text-body">{description}</span>}
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="sr-only"
      />
    </label>
  )
}

interface FormSectionProps {
  title: string
  description?: string
  /** Optional inline marker (e.g. a required/complete `FieldMarker`) rendered next to the title. */
  titleMarker?: React.ReactNode
  children: React.ReactNode
}

export function FormSection({ title, description, titleMarker, children }: FormSectionProps) {
  return (
    <div className="flex flex-col gap-2">
      <p className="flex items-center text-sm font-medium leading-4 text-text-heading">
        {title}
        {titleMarker}
      </p>
      {description && <p className="text-xs font-medium leading-5 text-text-body">{description}</p>}
      <div className="flex flex-col">{children}</div>
    </div>
  )
}

function formatTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000)

  if (seconds < 5) return 'Saved just now'
  if (seconds < 30) return 'Saved moments ago'
  if (seconds < 60) return 'Saved less than a minute ago'
  if (seconds < 120) return 'Saved a minute ago'
  if (seconds < 3600) return `Saved ${Math.floor(seconds / 60)} minutes ago`
  return 'Saved'
}

interface SaveIndicatorProps {
  lastSaveTime: number | null
  isSaving?: boolean
}

export function SaveIndicator({ lastSaveTime, isSaving = false }: SaveIndicatorProps) {
  const [, forceUpdate] = useState(0)

  useEffect(() => {
    if (!lastSaveTime) return

    const interval = setInterval(() => {
      forceUpdate((n) => n + 1)
    }, 10000)

    return () => clearInterval(interval)
  }, [lastSaveTime])

  if (!lastSaveTime && !isSaving) return null

  return (
    <motion.div initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-1.5">
      <motion.div
        key={lastSaveTime}
        initial={{ scale: 1.2 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.2 }}
        className="flex h-4 w-4 items-center justify-center rounded-full bg-success-100"
      >
        <Check className="h-3 w-3 text-success-600" />
      </motion.div>
      <motion.span
        key={`text-${lastSaveTime}`}
        initial={{ opacity: 0.5 }}
        animate={{ opacity: 1 }}
        className="text-xs text-text-body"
      >
        {isSaving ? 'Saving...' : lastSaveTime ? formatTimeAgo(lastSaveTime) : ''}
      </motion.span>
    </motion.div>
  )
}

interface ModalFooterProps {
  onDiscard: () => void
  onClose?: () => void
  discardLabel?: string
  closeLabel?: string
  /** Optional icon rendered after the primary button label (e.g. a "Continue →" arrow). */
  closeIcon?: React.ReactNode
  lastSaveTime?: number | null
  isSaving?: boolean
  /** When true, Done is non-interactive (e.g. required fields or invalid email/phone). */
  closeDisabled?: boolean
  /**
   * When provided, renders a brand "Continue to …" CTA (label from
   * `continueLabel`) that advances to the next section modal. The "Done" button
   * is suppressed in this case so a chained modal shows a single primary action
   * (Discard + Continue-to only) — "Done" is the brand primary only when there
   * is no Continue-to (the last modal in a flow). Shares the
   * `closeDisabled`/`isSaving` gate with Done.
   */
  onContinue?: () => void
  continueLabel?: string
}

export function ModalFooter({
  onDiscard,
  onClose,
  discardLabel = 'Discard Changes',
  closeLabel = 'Done',
  closeIcon,
  lastSaveTime = null,
  isSaving = false,
  closeDisabled = false,
  onContinue,
  continueLabel = 'Continue',
}: ModalFooterProps) {
  const doneDisabled = Boolean(closeDisabled) || isSaving
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="flex-1" />
      <SaveIndicator lastSaveTime={lastSaveTime} isSaving={isSaving} />
      <button
        type="button"
        onClick={onDiscard}
        className="rounded-full border border-border-base bg-white px-4 py-2.5 text-sm font-medium text-text-heading shadow-xs transition-colors hover:bg-background-gray"
      >
        {discardLabel}
      </button>
      {onClose && !onContinue && (
        <button
          type="button"
          disabled={doneDisabled}
          onClick={onClose}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-600 px-4 py-2.5 text-sm font-medium text-white shadow-xs transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {closeLabel}
          {closeIcon}
        </button>
      )}
      {onContinue && (
        <button
          type="button"
          disabled={doneDisabled}
          onClick={onContinue}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-600 px-4 py-2.5 text-sm font-medium text-white shadow-xs transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {continueLabel}
          <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
        </button>
      )}
    </div>
  )
}

interface TextInputProps {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  required?: boolean
  type?: 'text' | 'email' | 'tel' | 'date'
  icon?: React.ReactNode
  max?: string
  /** Hard cap on input length (maps to the native `maxLength` attribute). */
  maxLength?: number
  error?: string
  showError?: boolean
  /**
   * Force the error styling (red border/label/icon) without rendering a
   * per-field message. Use when the field participates in a group-level
   * requirement whose explanation is shown elsewhere (e.g. an "email OR phone"
   * contact channel). `error`/`showError` still take precedence for field-level
   * messages when present.
   */
  invalid?: boolean
  /** Muted surface (e.g. guardian modal) — light gray fill + shadow-xs per design. */
  variant?: 'default' | 'soft'
  /** Muted guidance shown below the field when no error is displayed. */
  helperText?: string
  /**
   * Mirror the server-side `marshmallow.validate.Regexp(...)` allowlist. Defaults to ON for
   * `type='text'` and OFF for typed fields (`email`/`tel`/`date`) which carry their own validators
   * and naturally use characters the free-text allowlist forbids (e.g. `"`, `\`).
   */
  enforceFreeText?: boolean
  /**
   * Override the value used to decide whether the required-field marker is
   * "satisfied". Defaults to this field's own non-empty value. Supply this for a
   * *group* requirement (e.g. "email OR phone"): pass whether the group is
   * satisfied so filling either field flips both markers to the green check
   * instead of leaving the empty sibling showing an "owes" swatch.
   */
  markerFilled?: boolean
  /**
   * Explicit override for the input's `aria-required` attribute. Defaults to
   * `required`. Set to `false` for members of an OR-group (e.g. "email OR
   * phone") so assistive tech does not announce each field as individually
   * required — the group requirement is conveyed by the shared message
   * beneath the fields. The visual rail/marker (driven by `required`) still
   * signals that the *group* is required.
   */
  ariaRequired?: boolean
  /** Native disabled. Helper text stays available and is linked with aria-describedby. */
  disabled?: boolean
  /**
   * Extra id(s) merged into `aria-describedby` (space-separated). Use for a
   * shared helper that lives outside this field (e.g. one under-18 note for
   * both email and phone).
   */
  describedBy?: string
}

function getTodayDateString(): string {
  const today = new Date()
  return today.toISOString().split('T')[0]
}

export function TextInput({
  label,
  value,
  onChange,
  placeholder,
  required,
  type = 'text',
  icon,
  max,
  maxLength,
  error,
  showError = false,
  invalid = false,
  variant = 'default',
  helperText,
  enforceFreeText,
  markerFilled,
  ariaRequired,
  disabled = false,
  describedBy,
}: TextInputProps) {
  const fieldErrorId = useId()
  // Surface a field-level `error` once the user has left the field (blur), even
  // when the consumer hasn't flipped `showError` yet (which usually only happens
  // on submit). Disallowed-character (free-text) errors still show immediately.
  const [touched, setTouched] = useState(false)
  const maxDate = type === 'date' ? (max ?? getTodayDateString()) : undefined
  const isSoft = variant === 'soft'
  // Free-text validation is opt-in for typed fields (email/tel/date) which naturally use
  // characters the allowlist forbids (`"`, `\`, etc.).
  const enforceFreeTextResolved = enforceFreeText ?? type === 'text'
  const freeTextErr = enforceFreeTextResolved ? freeTextError(value) : undefined
  // Surface free-text errors immediately — disallowed characters are a hard validation
  // failure regardless of the consumer's `showError` flag (which usually gates required-ness).
  const effectiveError = freeTextErr ?? error
  const showEffectiveError = Boolean(freeTextErr) || Boolean((showError || touched) && error)
  const hasMessage = Boolean(showEffectiveError && effectiveError)
  // `invalid` forces the error styling even when there is no per-field message
  // (e.g. a group-level "email OR phone" requirement explained elsewhere).
  const hasError = hasMessage || invalid
  const errorMessageId = hasMessage ? `${fieldErrorId}-message` : undefined
  const helperTextId = helperText && !hasError ? `${fieldErrorId}-helper` : undefined
  const ariaDescribedBy =
    [errorMessageId, helperTextId, describedBy].filter(Boolean).join(' ') || undefined

  const surfaceClasses = hasError
    ? isSoft
      ? 'border-error-500 bg-error-50 shadow-xs placeholder:text-error-700/60 dark:border-error-500 dark:bg-gray-900 dark:placeholder:text-error-300/50'
      : 'border-error-500 bg-error-50 placeholder:text-error-700/60 dark:border-error-500 dark:bg-gray-900 dark:placeholder:text-error-300/50'
    : isSoft
      ? 'border-border-base bg-background-gray shadow-xs dark:border-gray-600 dark:bg-gray-800/80'
      : 'border-border-base bg-white dark:border-gray-600 dark:bg-gray-900'

  const markerState = resolveFieldMarkerState(Boolean(required), markerFilled ?? value.trim().length > 0)

  return (
    <div className="flex items-stretch gap-2">
      <FieldRequiredRail state={markerState} />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="mb-1.5 flex items-center">
          <label
            className={`text-sm font-medium ${hasError ? 'text-error-600 dark:text-error-400' : 'text-text-heading'}`}
            htmlFor={fieldErrorId}
          >
            {label}
          </label>
          <FieldMarker state={markerState} fieldLabel={label} />
        </div>
      <div className="relative">
        {icon && (
          <div
            className={`absolute left-3 top-1/2 -translate-y-1/2 ${hasError ? 'text-error-600 dark:text-error-400' : 'text-text-body'}`}
          >
            {icon}
          </div>
        )}
        <input
          id={fieldErrorId}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder={placeholder}
          max={maxDate}
          maxLength={maxLength}
          aria-required={(ariaRequired ?? required) || undefined}
          aria-invalid={hasError}
          aria-describedby={ariaDescribedBy}
          disabled={disabled}
          className={`w-full rounded-xl border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-background-disabled disabled:text-text-fg-disabled disabled:opacity-70 dark:disabled:bg-gray-800 dark:disabled:text-gray-500 ${
            hasError
              ? 'text-error-800 focus:border-error-600 focus:ring-error-500/25 dark:text-error-100'
              : 'text-text-heading focus:ring-brand-600/20 focus:border-brand-600 placeholder:text-text-body/50'
          } ${icon ? 'pl-10' : ''} ${surfaceClasses}`}
        />
      </div>
      {hasMessage && effectiveError ? (
        <p id={errorMessageId} className="mt-2.5 text-sm text-error-600 dark:text-error-400" role="alert">
          {effectiveError}
        </p>
      ) : helperText && !invalid ? (
        <p id={helperTextId} className="mt-2.5 text-xs font-medium text-text-body-subtle dark:text-gray-400">
          {helperText}
        </p>
      ) : null}
      </div>
    </div>
  )
}

interface TextareaInputProps {
  label?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  rows?: number
  required?: boolean
  error?: string
  showError?: boolean
  className?: string
  helperText?: string
  /** Mirror server-side `marshmallow.validate.Regexp(...)` allowlist. Defaults to ON. */
  enforceFreeText?: boolean
  /**
   * When true, disallowed free-text characters are visually highlighted inline
   * (rendered via a synced backdrop behind the textarea) so the user can see
   * exactly which characters to remove. Only meaningful with `enforceFreeText`.
   */
  highlightInvalidChars?: boolean
  /**
   * Current count for an optional character counter shown under the field. When the field
   * participates in a shared/combined budget (e.g. the clinical description), the caller
   * passes the combined count rather than `value.length`.
   */
  characterCount?: number
  /** Limit paired with `characterCount`. When both are set, a `count / limit` counter renders. */
  characterLimit?: number
  /** When true, the counter renders in an over-limit (error) style. */
  characterCountOverLimit?: boolean
}

/**
 * Split `value` into React nodes, wrapping each run of disallowed free-text
 * characters in a highlighted `<mark>` while leaving allowed text as plain
 * nodes. Used by the {@link TextareaInput} highlight backdrop so the invalid
 * characters line up exactly with the (transparent) textarea text above it.
 */
function renderInvalidCharSegments(value: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = []
  let buffer = ''
  let invalidBuffer = ''
  let key = 0

  const flushValid = () => {
    if (buffer) {
      nodes.push(<span key={key++}>{buffer}</span>)
      buffer = ''
    }
  }
  const flushInvalid = () => {
    if (invalidBuffer) {
      nodes.push(
        <mark
          key={key++}
          className="rounded-sm bg-error-500/40 text-error-700 dark:bg-error-500/40 dark:text-error-100"
        >
          {invalidBuffer}
        </mark>
      )
      invalidBuffer = ''
    }
  }

  for (const char of value) {
    if (isAllowedFreeTextChar(char)) {
      flushInvalid()
      buffer += char
    } else {
      flushValid()
      invalidBuffer += char
    }
  }
  flushValid()
  flushInvalid()
  return nodes
}

/**
 * Remove Tailwind background utilities (`bg-*`, incl. `dark:`/other variants and
 * opacity modifiers) from a className string. Used to neutralize any opaque
 * background — whether from this component's own styles or a consumer-supplied
 * `className` — on the textarea while the invalid-character highlight backdrop
 * is mounted behind it. Without this, conflicting `bg-*` utilities resolve by
 * stylesheet source order (not class-string order), so an opaque background
 * typically wins and fully covers the backdrop, hiding the highlight.
 */
function stripBackgroundClasses(className: string | undefined): string {
  if (!className) return ''
  return className
    .split(/\s+/)
    .filter((token) => {
      if (!token) return false
      // Drop `bg-...` whether bare or prefixed by variants (e.g. `dark:bg-...`,
      // `hover:bg-...`). Keep everything else (borders, gradients via `from-`/`to-`, etc.).
      const base = token.includes(':') ? token.slice(token.lastIndexOf(':') + 1) : token
      return !base.startsWith('bg-')
    })
    .join(' ')
}

export const TextareaInput = forwardRef<HTMLTextAreaElement, TextareaInputProps>(
  (
    {
      label,
      value,
      onChange,
      placeholder,
      rows = 3,
      required,
      error,
      showError = false,
      className,
      helperText,
      enforceFreeText = true,
      highlightInvalidChars = false,
      characterCount,
      characterLimit,
      characterCountOverLimit = false,
    },
    ref
  ) => {
    const fieldErrorId = useId()
    const [touched, setTouched] = useState(false)
    const freeTextErr = enforceFreeText ? freeTextError(value) : undefined
    const effectiveError = freeTextErr ?? error
    const showEffectiveError = Boolean(freeTextErr) || Boolean((showError || touched) && error)
    const hasError = Boolean(showEffectiveError && effectiveError)
    const errorMessageId = hasError ? `${fieldErrorId}-message` : undefined
    const helperId = helperText ? `${fieldErrorId}-helper` : undefined
    const showCounter = typeof characterCount === 'number' && typeof characterLimit === 'number'
    const counterId = showCounter ? `${fieldErrorId}-counter` : undefined
    const ariaDescribedBy = [errorMessageId, helperId, counterId].filter(Boolean).join(' ') || undefined

    // Inline highlight backdrop: only mounts when highlighting is enabled and
    // the value actually contains a disallowed character. The backdrop mirrors
    // the textarea's text (transparent text + colored <mark> on the bad chars)
    // and is kept scroll-synced with the textarea it sits behind.
    const enableHighlight = highlightInvalidChars && enforceFreeText
    const hasInvalidChars = enableHighlight && Boolean(freeTextErr)
    const backdropRef = useRef<HTMLDivElement>(null)
    const textareaRef = useRef<HTMLTextAreaElement | null>(null)
    const setTextareaRef = useCallback(
      (node: HTMLTextAreaElement | null) => {
        textareaRef.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node
      },
      [ref]
    )
    const syncBackdropScroll = useCallback(() => {
      const backdrop = backdropRef.current
      const textarea = textareaRef.current
      if (!backdrop || !textarea) return
      backdrop.scrollTop = textarea.scrollTop
      backdrop.scrollLeft = textarea.scrollLeft
    }, [])
    useEffect(() => {
      if (hasInvalidChars) syncBackdropScroll()
    }, [hasInvalidChars, value, syncBackdropScroll])

    const markerState = resolveFieldMarkerState(Boolean(required), value.trim().length > 0)

    return (
      <div className="flex items-stretch gap-2">
        <FieldRequiredRail state={markerState} />
        <div className="flex min-w-0 flex-1 flex-col">
        {label && (
          <div className="mb-2.5 flex items-center">
            <label
              className={`text-sm font-medium ${hasError ? 'text-error-600 dark:text-error-400' : 'text-text-heading'}`}
              htmlFor={fieldErrorId}
            >
              {label}
            </label>
            <FieldMarker state={markerState} fieldLabel={label} />
          </div>
        )}
        <div className="relative">
          {hasInvalidChars ? (
            <div
              ref={backdropRef}
              aria-hidden="true"
              className={`pointer-events-none absolute inset-0 overflow-auto whitespace-pre-wrap break-words rounded-xl border border-transparent bg-white px-4 py-2.5 font-sans text-sm font-normal leading-5 tracking-normal text-transparent dark:bg-gray-900 ${className ?? ''}`}
            >
              {renderInvalidCharSegments(value)}
              {/* Trailing newline needs a spacer so the backdrop height matches the textarea. */}
              {value.endsWith('\n') ? '\u200b' : null}
            </div>
          ) : null}
          <textarea
            ref={setTextareaRef}
            id={fieldErrorId}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={() => setTouched(true)}
            onScroll={hasInvalidChars ? syncBackdropScroll : undefined}
            placeholder={placeholder}
            rows={rows}
            aria-required={required || undefined}
            aria-invalid={hasError}
            aria-describedby={ariaDescribedBy}
            className={`relative w-full resize-none whitespace-pre-wrap break-words rounded-xl border px-4 py-2.5 font-sans text-sm font-normal leading-5 tracking-normal focus:outline-none focus:ring-2 ${
              hasInvalidChars ? 'bg-transparent dark:bg-transparent' : ''
            } ${
              hasError
                ? `border-error-500 text-error-800 placeholder:text-error-700/60 focus:border-error-600 focus:ring-error-500/25 dark:border-error-500 dark:text-error-100 dark:placeholder:text-error-300/50 ${
                    hasInvalidChars ? '' : 'bg-white dark:bg-gray-900'
                  }`
                : `border-border-base text-text-heading placeholder:text-text-body/50 focus:ring-brand-600/20 focus:border-brand-600 dark:border-gray-600 ${
                    hasInvalidChars ? '' : 'bg-white dark:bg-gray-900'
                  }`
            } ${hasInvalidChars ? stripBackgroundClasses(className) : (className ?? '')}`}
          />
        </div>
        {helperText || showCounter ? (
          <div className="mt-2.5 flex items-start justify-between gap-4">
            {helperText ? (
              <p id={helperId} className="text-xs font-normal leading-5 text-text-body-subtle dark:text-gray-400">
                {helperText}
              </p>
            ) : (
              <span />
            )}
            {showCounter ? (
              <p
                id={counterId}
                className={`shrink-0 text-xs font-medium leading-5 tabular-nums ${
                  characterCountOverLimit
                    ? 'text-error-600 dark:text-error-400'
                    : 'text-text-body-subtle dark:text-gray-400'
                }`}
                aria-live="polite"
              >
                {characterCount!.toLocaleString()} / {characterLimit!.toLocaleString()}
              </p>
            ) : null}
          </div>
        ) : null}
        {hasError && effectiveError ? (
          <p id={errorMessageId} className="mt-2.5 text-sm text-error-600 dark:text-error-400" role="alert">
            {effectiveError}
          </p>
        ) : null}
        </div>
      </div>
    )
  }
)

TextareaInput.displayName = 'TextareaInput'

interface ModalValidationAlertProps {
  missingFields: string[]
  title?: string
}

export function ModalValidationAlert({
  missingFields,
  title = 'Required fields need attention',
}: ModalValidationAlertProps) {
  if (missingFields.length === 0) return null

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex gap-3 rounded-xl border border-red-200 bg-red-50 p-4"
    >
      <ExclamationCircle className="h-5 w-5 shrink-0 text-red-600" />
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium leading-5 text-red-800">{title}</p>
        <p className="text-sm leading-5 text-red-700">Please complete: {missingFields.join(', ')}</p>
      </div>
    </motion.div>
  )
}

interface ToggleProps {
  label: string
  description?: string
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  /** Larger label + slightly smaller track (biological parent modal / Figma R5). */
  size?: 'default' | 'compact' | 'panel'
}

export function Toggle({ label, description, checked, onChange, disabled = false, size = 'default' }: ToggleProps) {
  const id = useId()
  const labelId = `${id}-label`
  const descriptionId = description ? `${id}-description` : undefined
  const compactTrack = size === 'compact' || size === 'panel'
  const compactLabel = size === 'compact'
  const panel = size === 'panel'
  const compactOffBorder = compactTrack && !panel && !checked ? 'border border-border-base dark:border-gray-600' : ''
  // Any 1px border eats into the (border-box) track height, so bordered states
  // use a 1px inset and borderless states a 2px inset. Combined with the flex
  // `items-center`/`justify-*` layout below, the knob stays centered and never
  // touches an edge across every size + the bordered panel/compact-off variants.
  const hasBorder = panel || Boolean(compactOffBorder)
  const trackPadding = hasBorder ? 'p-px' : 'p-0.5'
  return (
    <div className={`flex items-start gap-2 ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}>
      <span className="relative inline-flex shrink-0">
        <input
          id={id}
          type="checkbox"
          role="switch"
          checked={checked}
          aria-checked={checked}
          disabled={disabled}
          aria-labelledby={labelId}
          aria-describedby={descriptionId}
          onChange={(e) => onChange(e.target.checked)}
          className="peer absolute inset-0 z-10 m-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
        />
        <span
          aria-hidden="true"
          className={`pointer-events-none relative flex items-center rounded-full transition-colors peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-600 ${trackPadding} ${checked ? 'justify-end' : 'justify-start'} ${panel ? `border ${checked ? 'border-brand-500' : 'border-border-base dark:border-gray-600'}` : ''} ${compactOffBorder} ${compactTrack ? (panel ? 'mt-0 h-5 w-9' : 'mt-1 h-5 w-9') : 'mt-0.5 h-6 w-11'} ${checked ? 'bg-brand-600' : compactTrack && !panel ? 'bg-zinc-200 dark:bg-gray-600' : 'bg-background-gray dark:bg-gray-600'}`}
        >
          <span
            className={`rounded-full bg-white shadow-sm transition-all ${compactTrack ? 'h-4 w-4' : 'h-5 w-5'}`}
          />
        </span>
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <label
          htmlFor={id}
          id={labelId}
          className={`cursor-pointer text-text-heading dark:text-white ${disabled ? 'cursor-not-allowed' : ''} ${compactLabel ? 'text-lg font-semibold tracking-tight' : panel ? 'text-sm font-medium leading-5' : 'text-sm font-medium'}`}
        >
          {label}
        </label>
        {description ? (
          <span
            id={descriptionId}
            className={`text-text-body dark:text-gray-400 ${panel ? 'text-sm font-normal leading-5' : 'text-xs'}`}
          >
            {description}
          </span>
        ) : null}
      </span>
    </div>
  )
}
